/**
 * One-way mirror: Doomscroll library is sourced from SiYuan's /Books tree
 * via the laptop sync worker's /books endpoint. On boot we fetch the
 * authoritative list and reconcile with local IndexedDB:
 *   - Titles present in SiYuan but missing locally: left alone (phone
 *     doesn't have the chapters cached, but the entry in SiYuan is the
 *     real existence signal - we could add a "download" path later).
 *   - Titles present locally but missing in SiYuan: marked removed
 *     (hidden from library). User deleted via Claude Code = gone from
 *     phone on next refresh.
 *   - Titles present in both: kept.
 *
 * Adds happen via normal upload flow (Doomscroll -> Supabase -> sync
 * worker -> SiYuan). Deletes only via Claude Code editing SiYuan.
 */

import { listBooks, getDB, type LocalBook } from './db';

const DEFAULT_SYNC_URL = 'https://shaunsmacbook.tail3e3e16.ts.net:8445';

export function getSyncUrl(): string {
  return (localStorage.getItem('doomscrollSyncUrl') || DEFAULT_SYNC_URL).replace(/\/+$/, '');
}

type RemoteBook = { id: string; title: string; hpath: string };

function norm(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

export async function fetchRemoteBooks(timeoutMs = 3500): Promise<RemoteBook[] | null> {
  const ac = new AbortController();
  const t = setTimeout(() => ac.abort(), timeoutMs);
  try {
    const res = await fetch(getSyncUrl() + '/books', { signal: ac.signal });
    clearTimeout(t);
    if (!res.ok) return null;
    const j: any = await res.json();
    if (!j?.ok) return null;
    return (j.books as RemoteBook[]) || [];
  } catch { clearTimeout(t); return null; }
}

/**
 * Reconcile local IndexedDB against remote SiYuan list. Non-destructive:
 * books not found remotely are marked hidden (so they stop appearing in
 * the library), not deleted - re-adding via SiYuan restores them.
 */
export async function reconcileLibrary(): Promise<{ hidden: number; total: number } | null> {
  const remote = await fetchRemoteBooks();
  if (!remote) return null;
  const remoteTitles = new Set(remote.map((b) => norm(b.title)));

  const local = await listBooks();
  const db = await getDB();
  let hidden = 0;

  for (const b of local) {
    // Seed books (builtin samples) bypass the mirror - always visible.
    if (b.kind === 'seed') continue;
    const present = remoteTitles.has(norm(b.title));
    const isHidden = (b as any).hidden === true;
    if (!present && !isHidden) {
      await db.put('books', { ...b, hidden: true } as LocalBook);
      hidden++;
    } else if (present && isHidden) {
      const { hidden: _, ...rest } = b as any;
      await db.put('books', rest as LocalBook);
    }
  }

  return { hidden, total: remote.length };
}
